/**
 * EditCategoryModal
 * -----------------
 * Modal compacto para que el director (o cualquier usuario activo con
 * permisos sobre la inscripción) cambie únicamente el "tipo de entrada"
 * (categoría/pricing) de un conquistador ya inscrito, sin tener que cancelar
 * y volver a crear la inscripción.
 *
 * Reusa `useAdminEditRegistration` (que llama a `editFields`); el patch sólo
 * incluye `category` + `unitPrice` recalculado del pricing del evento (con
 * early-bird y descuento de distrito si aplican). Se registra entrada en
 * `eventRegistrationAudit`.
 *
 * NO permite cambiar club / estado / persona — esas operaciones siguen
 * reservadas al modal admin (`AdminRegistrationEditModal`).
 *
 * Hot-feature 2026-04-27 (FIEL CONQUISTADOR): los directores pidieron poder
 * corregir el tipo de inscripción sin perder la posición ni volver a hacer el
 * flujo de pago.
 */

import { useEffect, useMemo, useState } from "react";
import { Modal, ModalFooter, Button, Select, Input } from "@/components/ui";
import { useAdminEditRegistration } from "@/hooks/useEvents";
import { getActiveAmount, isClubDiscounted } from "@/lib/pricing";
import { useQuery } from "@tanstack/react-query";
import { churchService } from "@/lib/services/organization";
import type { Event, EventRegistration } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registration: EventRegistration | null;
  event: Event;
  /** Iglesia del club para resolver el descuento por distrito (opcional). */
  clubChurchId?: string;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);

export default function EditCategoryModal({
  isOpen,
  onClose,
  registration,
  event,
  clubChurchId,
}: Props) {
  console.log("[EditCategoryModal] render", {
    isOpen,
    hasRegistration: !!registration,
    regId: registration?.id,
  });
  const editMutation = useAdminEditRegistration();
  const [category, setCategory] = useState<string>(
    registration?.category ?? "",
  );

  // Resolver descuento por distrito (igual que EventRegistrationModal)
  const eventHasDistrictDiscount =
    !!event.discountedDistrictIds?.length;
  const { data: clubChurch } = useQuery({
    queryKey: ["church", clubChurchId],
    queryFn: () =>
      clubChurchId
        ? churchService.getById(clubChurchId)
        : Promise.resolve(null),
    enabled: !!clubChurchId && eventHasDistrictDiscount,
    staleTime: 5 * 60 * 1000,
  });
  const isDiscounted = useMemo(
    () =>
      isClubDiscounted(
        clubChurch?.districtId ?? clubChurch?.alfoliDistrictId,
        event,
      ),
    [clubChurch?.districtId, clubChurch?.alfoliDistrictId, event],
  );

  // Reset al abrir con otra inscripción
  useEffect(() => {
    if (registration) setCategory(registration.category);
  }, [registration]);

  if (!registration) return null;

  const pricingOptions = event.pricing ?? [];
  const selectedPricing = pricingOptions.find((p) => p.category === category);
  // El precio se calcula con la fecha en que la persona SE INSCRIBIÓ, no con
  // la de hoy: el pronto pago ya se lo ganó y cambiarle el tipo de entrada no
  // se lo quita. Sin esto, editar una inscripción después del vencimiento la
  // encarecía — a Emilys Lisset (KÁMELEC), inscrita el 11/8, le subió de
  // $80.000 a $90.000 el 21/9 sólo por pasarla de consejera a padre
  // acompañante. Las dos categorías valen lo mismo.
  const momentoDeInscripcion =
    (registration.registrationDate as { toMillis?: () => number })?.toMillis?.() ??
    undefined;
  const newUnitPrice = selectedPricing
    ? getActiveAmount(selectedPricing, isDiscounted, momentoDeInscripcion)
    : registration.unitPrice;

  // Recalcular paymentStatus tras el cambio de precio.
  // Reglas:
  //  - amountPaid <= 0  → "pending"
  //  - amountPaid >= newUnitPrice → "paid"
  //  - intermedio → "partial"
  // Nunca tocamos "refunded" (estado terminal).
  const computeNewPaymentStatus = (
    paid: number,
    price: number,
    current: EventRegistration["paymentStatus"],
  ): EventRegistration["paymentStatus"] => {
    if (current === "refunded") return current;
    if (paid <= 0) return "pending";
    if (paid + 0.0001 >= price) return "paid";
    return "partial";
  };
  const newPaymentStatus = computeNewPaymentStatus(
    registration.amountPaid,
    newUnitPrice,
    registration.paymentStatus,
  );
  const balance = newUnitPrice - registration.amountPaid;

  const hasChange = category !== registration.category;

  const handleSave = async () => {
    if (!hasChange || !selectedPricing) {
      onClose();
      return;
    }
    try {
      const patch: Parameters<
        typeof editMutation.mutateAsync
      >[0]["patch"] = {
        category: category as EventRegistration["category"],
        unitPrice: newUnitPrice,
      };
      // Solo enviar paymentStatus si efectivamente cambió, para evitar writes
      // innecesarios y mantener el audit log limpio.
      if (newPaymentStatus !== registration.paymentStatus) {
        patch.paymentStatus = newPaymentStatus;
      }
      await editMutation.mutateAsync({
        registrationId: registration.id,
        eventId: registration.eventId,
        patch,
        reason: "Cambio de tipo de entrada (director)",
      });
      onClose();
    } catch {
      // toast manejado por el hook
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cambiar tipo de entrada · ${registration.personName}`}
      size="sm"
    >
      <div className="space-y-4 p-4">
        <div className="text-sm text-gray-600">
          <p>
            Tipo actual:{" "}
            <strong>
              {pricingOptions.find(
                (p) => p.category === registration.category,
              )?.label || registration.category}
            </strong>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Precio actual: {formatCurrency(registration.unitPrice)}
          </p>
        </div>

        <Select
          label="Nuevo tipo de entrada"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {pricingOptions.map((p) => (
            <option key={p.category} value={p.category}>
              {p.label} — {formatCurrency(getActiveAmount(p, isDiscounted))}
            </option>
          ))}
        </Select>

        {hasChange && selectedPricing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium text-amber-800">
              Nuevo precio: {formatCurrency(newUnitPrice)}
            </p>
            {newUnitPrice !== registration.unitPrice && (
              <p className="text-xs text-amber-700">
                Diferencia:{" "}
                <strong>
                  {newUnitPrice > registration.unitPrice ? "+" : ""}
                  {formatCurrency(newUnitPrice - registration.unitPrice)}
                </strong>
              </p>
            )}
            <p className="text-xs text-amber-700">
              Pagado: {formatCurrency(registration.amountPaid)} · Saldo:{" "}
              <strong>{formatCurrency(Math.max(0, balance))}</strong>
            </p>
            {newPaymentStatus !== registration.paymentStatus && (
              <p className="text-xs text-amber-800">
                Estado de pago:{" "}
                <strong>{registration.paymentStatus}</strong> →{" "}
                <strong>{newPaymentStatus}</strong>
              </p>
            )}
            {balance < 0 && (
              <p className="text-xs text-red-700 mt-1">
                ⚠️ El nuevo precio es menor al monto pagado. Hay un excedente
                de {formatCurrency(-balance)} que debe gestionarse con
                tesorería (no se reembolsa automáticamente).
              </p>
            )}
            {registration.amountPaid > 0 &&
              newUnitPrice > registration.unitPrice && (
                <p className="text-xs text-amber-700">
                  ⚠️ Esta inscripción ya tiene pagos. Verifica con tesorería
                  el cobro de la diferencia.
                </p>
              )}
          </div>
        )}

        <Input
          label="Resumen"
          value={`${registration.personName} → ${selectedPricing?.label || category}`}
          readOnly
          className="bg-gray-50"
        />
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChange || editMutation.isPending}
        >
          {editMutation.isPending ? "Guardando…" : "Guardar cambio"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
